import type { Carer } from '@lmc/ts-common'
import type { UserDtos } from '@lmc/ts-common/lib/dtos/User'
import { Avatar, EmptyState, NotStartedIcon, Tag } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import type {
  NoticeListItemResponse,
  NoticeUserShortPicture
} from '@lmc/validations'
import { Table, type TableProps } from 'antd'
import type { SorterResult } from 'antd/es/table/interface'
import { formatDateToDay } from 'common/utils'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { noticeboardConstants } from 'locale/noticeboard'
import type { Dispatch, Key, SetStateAction } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import type { NoticeboardListFilter } from '../hooks/useNotices'

export const noticeboardDefaultSort = {
  updatedOn: -1,
  createdOn: -1
} as const

export type NoticeboardSortObject = Partial<
  Record<keyof Omit<NoticeListItemResponse, 'seenBy'> | 'seen', 1 | -1>
>

type NoticesTableProps = {
  notices: NoticeListItemResponse[]
  page: number
  setPage: Dispatch<SetStateAction<number>>
  count: number
  isLoading: boolean
  onSortChange: (sort: NoticeboardSortObject) => void
  listFilter: NoticeboardListFilter
  sortParams: NoticeboardSortObject
}

type DataSource = {
  noticeId: string
  titleString: string
  title: NoticeListItemResponse
  body: string
  updatedBy: Pick<UserDtos, 'name' | 'id' | 'picture'>
  updatedOn: string
  seenBy: string[]
  view: string
}

const NoticesTable = ({
  notices,
  page,
  setPage,
  count,
  isLoading,
  onSortChange,
  listFilter,
  sortParams
}: NoticesTableProps) => {
  const getSortOrder = (
    key: keyof NoticeboardSortObject
  ): 'ascend' | 'descend' | undefined => {
    return sortParams?.[key] === 1
      ? 'ascend'
      : sortParams?.[key] === -1
        ? 'descend'
        : undefined
  }

  const { t } = useTranslation(NAMESPACE.NOTICEBOARD)

  const user = useSelector(
    (state: { global: { user: Carer } }) => state.global.user
  )

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const listSearch = searchParams.toString()
  const viewSearchSuffix = listSearch ? `?${listSearch}` : ''

  const handleRowClick = (record: DataSource) => {
    navigate({
      pathname: `/noticeboard/${record.noticeId}`,
      search: viewSearchSuffix
    })
  }

  const renderTitleCell = (notice: NoticeListItemResponse) => {
    if (listFilter === 'unread') {
      return (
        <StyledNoticeTitle>
          <Text weight="bold">{notice.title}</Text>
          <Tag color="magenta">{t(noticeboardConstants.UNREAD)}</Tag>
        </StyledNoticeTitle>
      )
    }

    const myID = user.id
    const isSeenByMe = notice.seenBy
      .map((u: NoticeUserShortPicture) => u.id)
      .includes(myID)
    const noticeActivityBeforeMe = dayjs(
      notice.updatedOn ?? notice.createdOn
    ).isBefore(dayjs(user.createdOn))
    if (isSeenByMe || noticeActivityBeforeMe) {
      return <Text>{notice.title}</Text>
    }
    return (
      <StyledNoticeTitle>
        <Text weight="bold">{notice.title}</Text>
        <Tag color="magenta">{t(noticeboardConstants.UNREAD)}</Tag>
      </StyledNoticeTitle>
    )
  }

  const columns = [
    {
      dataIndex: 'title',
      title: <Text weight="medium">{t(noticeboardConstants.TABLE_TITLE)}</Text>,
      key: 'title',
      sorter: true,
      sortOrder: getSortOrder('title'),
      render: (notice: NoticeListItemResponse) => renderTitleCell(notice)
    },
    {
      dataIndex: 'updatedOn',
      title: (
        <DataContainer>
          <NoBreakText weight="medium">
            {t(noticeboardConstants.LAST_UPDATED_ON)}
          </NoBreakText>
        </DataContainer>
      ),
      key: 'updatedOn',
      sorter: true,
      sortOrder: getSortOrder('updatedOn'),
      width: 120,

      render: (updatedOn: string) => (
        <DateContainer>
          <Text>{formatDateToDay(updatedOn)}</Text>
          <Text>{dayjs(updatedOn).format('HH:mm')}</Text>
        </DateContainer>
      )
    },
    {
      dataIndex: 'updatedBy',
      title: (
        <DataContainer>
          <Text weight="medium">{t(noticeboardConstants.LAST_UPDATED_BY)}</Text>
        </DataContainer>
      ),
      key: 'updatedBy',
      sorter: true,
      sortOrder: getSortOrder('updatedBy'),
      width: 290,

      render: (updatedBy: DataSource['updatedBy']) => (
        <PersonCard>
          <Avatar url={updatedBy?.picture} />
          <Text>{updatedBy?.name}</Text>
        </PersonCard>
      )
    },

    {
      dataIndex: 'view',
      title: ' ',
      key: 'view',
      width: 56,
      render: (noticeId: string) => (
        <Link to={`/noticeboard/${noticeId}${viewSearchSuffix}`}>
          <Text weight="medium">{t(noticeboardConstants.VIEW)}</Text>
        </Link>
      )
    }
  ]

  const mobileColumns = columns.filter((column) => column.key !== 'updatedBy')

  const dataSource: DataSource[] = notices?.map(
    (notice: NoticeListItemResponse): DataSource => ({
      noticeId: notice.id,
      titleString: notice.title,
      title: notice,
      body: notice.body,
      updatedBy: notice.updatedBy || notice.createdBy,
      updatedOn: notice.updatedOn?.toString() || notice.createdOn.toString(),
      seenBy: notice.seenBy.map(
        (user: NoticeUserShortPicture) => user?.picture
      ),
      view: notice.id
    })
  )

  const handleTableChange: TableProps<DataSource>['onChange'] = (
    _pagination,
    _filters,
    sorter
  ) => {
    const sortObject: NoticeboardSortObject = {}

    const applySortOrder = (s: SorterResult<DataSource>) =>
      s.order ? { [s.columnKey as Key]: getSortNumber(s.order) } : {}

    if (Array.isArray(sorter)) {
      for (const item of sorter) {
        Object.assign(sortObject, applySortOrder(item))
      }
    } else {
      Object.assign(sortObject, applySortOrder(sorter))
    }

    onSortChange(sortObject)
  }

  if (!notices.length && !isLoading) {
    if (listFilter === 'unread') {
      return (
        <EmptyState
          icon={<NotStartedIcon />}
          title={t(noticeboardConstants.EMPTY_UNREAD_TITLE)}
          description={t(noticeboardConstants.EMPTY_UNREAD_DESCRIPTION)}
        />
      )
    }
    return (
      <EmptyState
        icon={<NotStartedIcon />}
        title={t(noticeboardConstants.NO_NOTICES_YET)}
        description={t(noticeboardConstants.NO_NOTICES_YET_DESCRIPTION)}
      />
    )
  }

  return (
    <TableContainer>
      <Table<DataSource>
        onChange={handleTableChange}
        loading={isLoading}
        dataSource={dataSource}
        columns={isMobile ? mobileColumns : columns}
        rowKey="noticeId"
        onRow={(record) => ({
          onClick: () => handleRowClick(record)
        })}
        components={{
          body: {
            row: ClickableTableRow
          }
        }}
        pagination={{
          pageSize: 10,
          current: page,
          onChange: (page) => {
            setPage(page)
          },
          total: count,
          showSizeChanger: false,
          position: ['bottomCenter']
        }}
      />
    </TableContainer>
  )
}

const getSortNumber = (sortOrder: 'ascend' | 'descend'): 1 | -1 =>
  sortOrder === 'ascend' ? 1 : -1

const TableContainer = styled.div`
  margin-top: 24px;
  .ant-table-column-sorters {
    gap: 8px;
    flex-direction: row-reverse;
  }
  .ant-table-wrapper,
  .ant-table-container {
    border-radius: 16px;
    overflow: hidden;
  }
  .ant-table,
  .ant-table-header,
  .ant-table-tbody > tr > td,
  .ant-table-thead > tr > th {
    background: transparent;
  }
`

const ClickableTableRow = styled.tr`
  cursor: pointer;
`

const StyledNoticeTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const NoBreakText = styled(Text)`
  white-space: nowrap;
`

const DataContainer = styled.div``

const DateContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: start;
`

const PersonCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 8px;
`

export default NoticesTable
